import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const microeconomicsUnit5Notes: UnitNotes = {
  unitId: "unit-5",
  unitName: "Unit 5: Factor Markets",
  examWeight: "10–13%",
  focus: "The 'Upside Down' World: Analyzing how firms hire resources and how wages are determined in competitive and monopsonistic markets.",
  sections: [
    {
      id: "unit-5-overview",
      title: "Unit 5 Overview: The 'Upside Down' World",
      content: (
        <div className="space-y-4">
          <p className="text-slate-700 leading-relaxed">
            In Unit 5, the roles from previous units are reversed:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-900">🏠 Households</h4>
              <p className="text-sm text-slate-700 mt-1">Now the <strong>suppliers</strong> (selling labor, land, and capital).</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <h4 className="font-bold text-indigo-900">🏭 Firms</h4>
              <p className="text-sm text-slate-700 mt-1">Now the <strong>demanders</strong> (buying resources to produce goods).</p>
            </div>
          </div>
          <p className="text-slate-700 text-sm italic">
            <strong>Circular Flow:</strong> We are now analyzing the bottom half of the circular flow diagram (the Resource Market).
          </p>
        </div>
      )
    },
    {
      id: "topic-5-1",
      title: "Topic 5.1: Introduction to Factor Markets",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏗️ 1. Factors of Production</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-center">
                <p className="font-bold text-slate-900">Land</p>
                <p className="text-xs text-slate-600">Payment: Rent</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-center">
                <p className="font-bold text-slate-900">Labor</p>
                <p className="text-xs text-slate-600">Payment: Wage</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-center">
                <p className="font-bold text-slate-900">Capital</p>
                <p className="text-xs text-slate-600">Payment: Interest</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-center">
                <p className="font-bold text-slate-900">Entrepreneurship</p>
                <p className="text-xs text-slate-600">Payment: Profit</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔗 2. Derived Demand</h3>
            <KeyTerm 
              term="Derived Demand" 
              definition="The demand for a resource is derived from the demand for the product that resource produces."
            />
            <div className="mt-2 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
              <p className="text-sm text-slate-700"><strong>Example:</strong> If the demand for sushi increases, the demand for sushi chefs (labor) and rice (land) will subsequently increase. If nobody wants sushi, nobody hires sushi chefs.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 3. Marginal Analysis in Factor Markets</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-bold text-slate-900">A. Marginal Product (MP)</h4>
                <p className="text-sm text-slate-600 mb-2">The additional output produced by hiring one more unit of input.</p>
                <BlockMath math="MP = \frac{\Delta \text{Total Product}}{\Delta \text{Inputs}}" />
                <p className="text-xs text-slate-500 italic mt-1">Note: Diminishing Marginal Returns applies here. As you add more workers to fixed capital, MP eventually falls.</p>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">B. Marginal Revenue Product (MRP)</h4>
                <p className="text-sm text-slate-600 mb-2">The change in Total Revenue resulting from one more unit of input. It serves as the <strong>Factor Demand Curve</strong>.</p>
                <div className="space-y-2">
                  <BlockMath math="MRP = \frac{\Delta \text{Total Revenue}}{\Delta \text{Inputs}}" />
                  <BlockMath math="MRP = \text{Marginal Product (MP)} \times \text{Marginal Revenue (MR)}" />
                </div>
                <p className="text-xs text-blue-700 font-semibold mt-2">In Perfect Competition: Since P = MR, then MRP = MP × P.</p>
              </div>

              <div>
                <h4 className="font-bold text-slate-900">C. Marginal Resource Cost (MRC)</h4>
                <p className="text-sm text-slate-600 mb-2">Also called Marginal Factor Cost (MFC). The additional cost incurred by employing one more unit of input.</p>
                <BlockMath math="MRC = \frac{\Delta \text{Total Resource Cost}}{\Delta \text{Inputs}}" />
                <p className="text-xs text-slate-700 mt-1"><strong>In a Perfectly Competitive Labor Market:</strong> The firm is a "wage taker," so <InlineMath math="MRC = \text{Wage}" />.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ 4. The Profit-Maximizing Hiring Rule</h3>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <h4 className="font-bold text-amber-900">Rule: Hire where MRP = MRC</h4>
              <div className="grid md:grid-cols-2 gap-4 mt-3">
                <div className="bg-white p-3 rounded border border-amber-100">
                  <p className="text-sm text-slate-700"><strong>MRP {'>'} MRC:</strong> Hire more (you make profit on that worker).</p>
                </div>
                <div className="bg-white p-3 rounded border border-amber-100">
                  <p className="text-sm text-slate-700"><strong>MRP {'<'} MRC:</strong> Fire/Hire fewer (you lose money on that worker).</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-2",
      title: "Topic 5.2: Changes in Factor Demand and Factor Supply",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 1. Shifters of Factor Demand (The MRP Curve)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-slate-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">Product Price Changes</h4>
                <p className="text-sm text-slate-700 mt-1">If the price of the product the worker makes goes up, the worker becomes more valuable because <InlineMath math="MRP = MP \times P" />.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-slate-50 p-4 rounded-lg border-l-4 border-green-400">
                <h4 className="font-bold text-green-900">Productivity Changes (Technology/Training)</h4>
                <p className="text-sm text-slate-700 mt-1">If workers become more productive (MP rises), MRP rises and shifts right.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-slate-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">Price of Related Resources</h4>
                <ul className="list-disc pl-5 mt-2 space-y-2 text-sm text-slate-700">
                  <li><strong>Substitute Resources:</strong>
                    <ul className="list-circle pl-5 mt-1">
                      <li><em>Substitution Effect:</em> If robot prices fall, firms replace humans (Labor Demand falls).</li>
                      <li><em>Output Effect:</em> If robots are cheap, costs fall, output rises, and firms may need more humans to manage robots (Labor Demand rises).</li>
                    </ul>
                  </li>
                  <li><strong>Complementary Resources:</strong> If the price of lumber falls, homebuilders hire <em>more</em> carpenters to use the lumber.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 2. Shifters of Factor Supply</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h5 className="font-bold text-slate-800">Number of Workers</h5>
                <p className="text-xs text-slate-600 mt-1">Immigration, population growth, or changes in education requirements.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h5 className="font-bold text-slate-800">Govt/Social Norms</h5>
                <p className="text-xs text-slate-600 mt-1">Changes in retirement age, child labor laws, or leisure vs. work culture.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h5 className="font-bold text-slate-800">Values/Leisure</h5>
                <p className="text-xs text-slate-600 mt-1">If society values leisure more, labor supply shifts left.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h5 className="font-bold text-slate-800">Alternative Opportunities</h5>
                <p className="text-xs text-slate-600 mt-1">If wages in a similar industry rise, workers leave the current industry.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-3",
      title: "Topic 5.3: Profit-Maximizing Behavior in Perfectly Competitive Factor Markets",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏢 1. Market Characteristics</h3>
            <ul className="list-disc pl-6 space-y-1 text-slate-700">
              <li>Many small firms hiring many workers with identical skills.</li>
              <li>Wage is constant (set by the market).</li>
              <li>Workers and firms are <strong>Wage Takers</strong>.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 2. The Graphs (Side-by-Side)</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900 underline mb-2">The Market Graph</h4>
                <p className="text-xs text-slate-700"><strong>Axes:</strong> Wage (Y), Q Labor (X).</p>
                <p className="text-xs text-slate-700"><strong>Curves:</strong> X-shaped Supply (S) and Demand (D).</p>
                <p className="text-xs text-slate-700"><strong>Result:</strong> Sets the market wage <InlineMath math="W_e" />.</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900 underline mb-2">The Firm Graph</h4>
                <p className="text-xs text-slate-700"><strong>Supply (S_L):</strong> Perfectly Elastic (Horizontal). <InlineMath math="S_L = Wage = MRC" />.</p>
                <p className="text-xs text-slate-700"><strong>Demand (D_L):</strong> Downward Sloping. <InlineMath math="D_L = MRP" />.</p>
                <p className="text-xs text-slate-700"><strong>Hiring:</strong> Where horizontal Supply intersects MRP.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💸 3. Cost Minimization Rule</h3>
            <p className="text-sm text-slate-700 mb-4">The "bang for the buck" (Marginal Product per dollar) must be equal for all inputs.</p>
            <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 text-center mb-4">
              <BlockMath math="\frac{MP_{\text{Labor}}}{\text{Price}_{\text{Labor}}} = \frac{MP_{\text{Capital}}}{\text{Price}_{\text{Capital}}}" />
            </div>
            <div className="space-y-3">
              <h4 className="font-bold text-slate-800">Optimization Strategy:</h4>
              <div className="bg-green-50 p-3 rounded border border-green-200">
                <p className="text-xs text-slate-700">If <InlineMath math="MP_L/P_L > MP_K/P_K" />: Labor is the better deal. Hire <strong>MORE Labor</strong> (lowers <InlineMath math="MP_L" />) and <strong>LESS Capital</strong> (raises <InlineMath math="MP_K" />) until equal.</p>
              </div>
              <div className="bg-amber-50 p-3 rounded border border-amber-200">
                <p className="text-xs text-slate-700">If <InlineMath math="MP_L/P_L < MP_K/P_K" />: Capital is the better deal. Hire <strong>MORE Capital</strong> and <strong>LESS Labor</strong>.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-4",
      title: "Topic 5.4: Monopsonistic Markets",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏭 1. Characteristics</h3>
            <ul className="list-disc pl-6 space-y-1 text-slate-700">
              <li><strong>Single buyer</strong> of labor (e.g., coal mine in a remote town).</li>
              <li>Worker is "immobile" (cannot easily leave).</li>
              <li>The firm is a <strong>Wage Maker</strong>.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 2. The Graph (The Distinctive Feature)</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-slate-700 mb-2"><strong>The MRC Curve:</strong> Upward sloping but <strong>higher</strong> and steeper than Supply.</p>
              <p className="text-xs text-slate-600 italic">Why? Because hiring the next worker requires raising the wage for all existing workers. Result: MRC {'>'} Wage.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📍 3. Determining Wage and Quantity</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="bg-slate-800 text-white rounded-full w-8 h-8 flex items-center justify-center shrink-0 font-bold">1</div>
                <div>
                  <h5 className="font-bold text-slate-900">Find Quantity</h5>
                  <p className="text-sm text-slate-600">Find where <strong>MRP = MRC</strong>. Go down to the X-axis (<InlineMath math="Q_m" />).</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-slate-800 text-white rounded-full w-8 h-8 flex items-center justify-center shrink-0 font-bold">2</div>
                <div>
                  <h5 className="font-bold text-slate-900">Find Wage</h5>
                  <p className="text-sm text-slate-600">From <InlineMath math="Q_m" />, go <strong>down to the Supply Curve</strong>, then across to the Y-axis (<InlineMath math="W_m" />).</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ 4. Comparison to Perfect Competition</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <ul className="list-disc pl-6 space-y-2 text-sm text-slate-700">
                <li><strong>Wage:</strong> Monopsonies pay a lower wage (<InlineMath math="W_{\text{monopsony}} < W_{\text{comp}}" />).</li>
                <li><strong>Quantity:</strong> Monopsonies hire fewer workers (<InlineMath math="Q_{\text{monopsony}} < Q_{\text{comp}}" />).</li>
                <li><strong>Inefficiency:</strong> Monopsonies create <strong>Deadweight Loss</strong> because they under-hire and under-pay relative to the allocatively efficient outcome.</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "unit-5-tips",
      title: "Unit 5 Exam Tips & Common Pitfalls",
      content: (
        <div className="space-y-4">
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h4 className="font-bold text-amber-900">1. Don't confuse MP with MRP</h4>
            <p className="text-sm text-slate-700 mt-1">MP is <strong>units</strong> (e.g., 5 apples); MRP is <strong>dollars</strong> (e.g., $10 worth of apples).</p>
          </div>

          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h4 className="font-bold text-red-900">2. The "Tax" Trick</h4>
            <ul className="list-disc pl-5 mt-1 text-sm text-slate-700">
              <li>If the govt taxes the <strong>product</strong>: Labor Demand (MRP) shifts left (MR is lower).</li>
              <li>If the govt taxes <strong>hiring</strong> (payroll tax): Labor Supply shifts left (MRC increases).</li>
            </ul>
          </div>

          <ImportantNote>
            <strong>Allocative Efficiency:</strong> Efficiency in labor occurs where <strong>Supply = Demand</strong> (or Supply = MRP). Monopsonies are inefficient because they operate where MRC = MRP.
          </ImportantNote>

          <div className="bg-slate-100 p-4 rounded-lg">
            <h4 className="font-bold text-slate-900 mb-2">3. Drawing Tip</h4>
            <p className="text-sm text-slate-700">When drawing a perfectly competitive factor market, always draw the <strong>Market graph first</strong> to establish the wage, then dot that wage across to create the Firm's horizontal supply curve.</p>
          </div>
        </div>
      )
    }
  ]
};

export default microeconomicsUnit5Notes;